// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Managed knowledge documents.
 *
 * The screen shows provenance — source, URI, and update time — because
 * `SEC-012` requires provenance and source-version citation for retrievable
 * content. Document bodies are never fetched here; the contract carries
 * metadata only and explicitly excludes binary payloads.
 */

import { capabilities, queryKeys, type ValidatedKnowledgeDocument } from '@eai/ui-shared';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

/** Render an ISO timestamp, or state that it was not reported. */
export function formatTimestamp(value: string | null): string {
  return value ?? 'Not reported';
}

/** Managed document screen. */
export function DocumentsScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.documents(scope),
    (client, signal) => client.listDocuments({ signal }),
  );

  return (
    <ResourceScreen<ValidatedKnowledgeDocument>
      title="Documents"
      description="Documents managed by the knowledge platform. Metadata and provenance only; document contents are not retrieved by this console."
      requiredCapability={capabilities.documentsRead}
      area="Documents"
      query={query}
      rowKey={(row) => row.document_id}
      emptyTitle="No document is managed"
      emptyDescription="Register an authorised source and run ingestion to populate this list."
      columns={[
        { key: 'title', header: 'Title', render: (row) => row.title },
        { key: 'source', header: 'Source', render: (row) => row.source ?? 'Not reported' },
        {
          key: 'contentType',
          header: 'Content type',
          render: (row) => row.content_type ?? 'Not reported',
        },
        { key: 'updated', header: 'Updated', render: (row) => formatTimestamp(row.updated_at) },
        {
          key: 'tags',
          header: 'Tags',
          render: (row) => (row.tags.length === 0 ? 'None' : row.tags.join(', ')),
        },
      ]}
    />
  );
}
